import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { 
  Bot, 
  MessageSquare,
  MessageCircle,
  X, 
  Send, 
  Minimize2, 
  Maximize2,
  Sparkles,
  Zap,
  ArrowRight,
  Star,
  HelpCircle,
  ShoppingCart,
  Briefcase,
  Users,
  Palette,
  Code,
  Megaphone,
  BarChart3,
  FileText,
  Video,
  CreditCard,
  Package,
  Settings,
  TrendingUp,
  Award,
  Globe,
  Phone,
  Cookie,
  Server,
  Shield
} from 'lucide-react';
import { apiUrl } from '../utils/api';
import DOMPurify from 'dompurify';

// Icon mapping for quick replies
const iconMap = {
  MessageCircle,
  ShoppingCart,
  Server,
  TrendingUp,
  Building: Briefcase,
  Globe,
  Code,
  Shield,
  Award,
  Sparkles,
  Settings,
  Megaphone,
  HelpCircle,
  Zap,
  Star,
  BarChart3,
  FileText,
  Video,
  CreditCard,
  Package,
  Users,
  Palette,
  Phone,
  Cookie
};

// Color gradient mapping
const getColorGradient = (color) => {
  const colorMap = {
    blue: 'from-blue-500 to-cyan-500',
    green: 'from-emerald-500 to-green-600',
    purple: 'from-[#7A5AFF] to-[#5a3fd9]',
    orange: 'from-orange-500 to-red-500',
    red: 'from-rose-500 to-pink-600',
    pink: 'from-rose-500 to-pink-600',
    indigo: 'from-indigo-500 to-blue-600',
    teal: 'from-teal-500 to-cyan-600',
    yellow: 'from-amber-500 to-yellow-600',
    gray: 'from-gray-600 to-slate-800'
  };
  return colorMap[color] || 'from-[#7A5AFF] to-[#5a3fd9]';
};

// Customer Form Component
const CustomerForm = ({ onSave }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    sector: '',
    gender: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name || !formData.phone || !formData.email || !formData.sector) {
      alert('Lütfen tüm alanları doldurun');
      return;
    }
    
    // Email format kontrolü
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert('Lütfen geçerli bir e-posta adresi girin');
      return;
    }

    // İsim ve soyisimi birleştir
    const customerData = {
      ...formData,
      full_name: `${formData.first_name} ${formData.last_name}`
    };

    setIsSubmitting(true);
    const success = await onSave(customerData);
    setIsSubmitting(false);

    if (success) {
      alert('Bilgileriniz kaydedildi! Artık GogiAI sizi isminizle hitap edecek.');
    } else {
      alert('Bilgiler kaydedilirken bir hata oluştu. Lütfen tekrar deneyin.');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Ad *
          </label>
          <Input
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            placeholder="Adınız"
            className="h-8 text-xs"
            required
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Soyad *
          </label>
          <Input
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
            placeholder="Soyadınız"
            className="h-8 text-xs"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Telefon *
        </label>
        <Input
          name="phone"
          type="tel"
          value={formData.phone}
          onChange={handleChange}
          placeholder="0555 123 45 67"
          className="h-8 text-xs"
          required
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          E-posta *
        </label>
        <Input
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="ornek@email.com"
          className="h-8 text-xs"
          required
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Sektör *
        </label>
        <Input
          name="sector"
          value={formData.sector}
          onChange={handleChange}
          placeholder="Sektörünüzü yazın (örn: e-ticaret, sağlık, eğitim)"
          className="h-8 text-xs"
          required
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Cinsiyet (Opsiyonel)
        </label>
        <div className="flex space-x-3">
          <label className="flex items-center text-xs">
            <input
              type="radio"
              name="gender"
              value="male"
              checked={formData.gender === 'male'}
              onChange={handleChange}
              className="mr-1"
            />
            Erkek
          </label>
          <label className="flex items-center text-xs">
            <input
              type="radio"
              name="gender"
              value="female"
              checked={formData.gender === 'female'}
              onChange={handleChange}
              className="mr-1"
            />
            Kadın
          </label>
        </div>
      </div>

      <div className="pt-2">
        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-blue-600 hover:bg-blue-700 h-8 text-xs"
        >
          {isSubmitting ? 'Kaydediliyor...' : 'Bilgilerimi Kaydet'}
        </Button>
      </div>
    </form>
  );
};

const EnhancedGogiWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sessionId, setSessionId] = useState('');
  const [customerInfo, setCustomerInfo] = useState(null);
  const [isConversationEnded, setIsConversationEnded] = useState(false);
  const [adminTyping, setAdminTyping] = useState(false);
  const [lastMessageSentAt, setLastMessageSentAt] = useState(null);
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [showCookieNotice, setShowCookieNotice] = useState(false);
  const [cookieAccepted, setCookieAccepted] = useState(false);
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(false);
  const [quickActions, setQuickActions] = useState([]);
  const messagesEndRef = useRef(null);
  // Güvenli session id üretici
  const generateSessionId = () => {
    try {
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return `gogiai_${crypto.randomUUID()}`;
      }
    } catch (_) {}
    const rand = Math.random().toString(36).slice(2);
    return `gogiai_${Date.now()}_${rand}`;
  };


  // Enhanced Categories
  const categories = [
    { 
      id: 'all', 
      name: 'Tümü', 
      icon: Bot, 
      color: 'from-[#7A5AFF] to-[#5a3fd9]',
      description: 'Genel sorular ve yardım'
    },
    { 
      id: 'ecommerce', 
      name: 'E-ticaret', 
      icon: ShoppingCart, 
      color: 'from-blue-500 to-cyan-500',
      description: 'Online mağaza ve satış'
    },
    { 
      id: 'design', 
      name: 'Tasarım', 
      icon: Palette, 
      color: 'from-pink-500 to-rose-500',
      description: 'Logo, web tasarım, grafik'
    },
    { 
      id: 'development', 
      name: 'Geliştirme', 
      icon: Code, 
      color: 'from-[#7A5AFF] to-[#5a3fd9]',
      description: 'Web, mobil, yazılım geliştirme'
    },
    { 
      id: 'marketing', 
      name: 'Pazarlama', 
      icon: Megaphone, 
      color: 'from-orange-500 to-red-500',
      description: 'SEO, sosyal medya, reklam'
    },
    { 
      id: 'business', 
      name: 'İş Danışmanlığı', 
      icon: BarChart3, 
      color: 'from-teal-500 to-green-500',
      description: 'Strateji, analiz, planlama'
    },
    { 
      id: 'content', 
      name: 'İçerik', 
      icon: FileText, 
      color: 'from-yellow-500 to-orange-500',
      description: 'Yazı, çeviri, editörlük'
    }
  ];

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Fetch quick replies from database
  useEffect(() => {
    const fetchQuickReplies = async () => {
      try {
        const res = await fetch(apiUrl('/backend/api.php?action=getGogiaiQuickReplies'));
        const data = await res.json();
        
        if (data.status === 'success' && Array.isArray(data.replies)) {
          // Transform database replies to quickActions format
          const actions = data.replies.map(reply => ({
            title: reply.title || reply.question.substring(0, 30),
            description: reply.description || '',
            icon: iconMap[reply.icon] || MessageSquare,
            color: getColorGradient(reply.color),
            action: reply.question
          }));
          setQuickActions(actions);
        }
      } catch (err) {
        console.error('Error fetching quick replies:', err);
        // Keep default quickActions if fetch fails
      }
    };
    
    fetchQuickReplies();
  }, []);

  // Session yönetimi
  useEffect(() => {
    const savedSessionId = localStorage.getItem('gogiai_session_id');
    if (savedSessionId) {
      setSessionId(savedSessionId);
      loadConversationHistory(savedSessionId);
      loadCustomerInfo(savedSessionId);
    } else {
      // Eğer session_id yoksa yeni bir tane oluştur
      const newSessionId = generateSessionId();
      setSessionId(newSessionId);
      localStorage.setItem('gogiai_session_id', newSessionId);
      //console.log('New session ID created:', newSessionId);
    }
  }, []);

  // Cookie bilgilendirmesi kontrolü - Kaldırıldı
  useEffect(() => {
    setCookieAccepted(true);
    setShowCookieNotice(false);
  }, []);

  // Polling: Yeni mesajları kontrol et (sadece widget açıkken ve konuşma devam ederken)
  useEffect(() => {
    if (!isOpen || isConversationEnded) return;
    
    // Mesaj gönderildikten sonra 5 saniye polling'i durdur (duplicate önlemek için)
    if (lastMessageSentAt && Date.now() - lastMessageSentAt < 5000) {
      return;
    }
    
    const pollInterval = setInterval(async () => {
      // Mesaj gönderildikten sonra 5 saniye polling'i durdur
      if (lastMessageSentAt && Date.now() - lastMessageSentAt < 5000) {
        return;
      }
      const currentSessionId = sessionId || localStorage.getItem('gogiai_session_id');
      if (!currentSessionId) return;
      
      try {
        const apiBase = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : `${window.location.protocol}//${window.location.host}`;
        const response = await fetch(`${apiBase}/backend/api.php?action=gogiai_conversation_history`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: currentSessionId, limit: 50 })
        });
        
        const data = await parseJsonResponse(response);
        if (data.conversations && data.conversations.length > 0) {
          // Yeni mesajları kontrol et
          const rows = [...data.conversations].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
          const newMessages = [];
          
          rows.forEach((conv) => {
            if (conv.user_message) {
              newMessages.push({
                id: `history_${conv.id}_u`,
                type: 'user',
                content: conv.user_message,
                timestamp: new Date(conv.created_at)
              });
            }
            if (conv.bot_response) {
              let cleanedResponse = conv.bot_response;
              // Suggestions'ı parse et
              const extractSuggestions = (text) => {
                const idx1 = text.lastIndexOf('[SUGGESTIONS:');
                if (idx1 !== -1) {
                  const start = idx1 + '[SUGGESTIONS:'.length;
                  let bracketCount = 0;
                  let jsonStart = -1;
                  let jsonEnd = -1;
                  for (let i = start; i < text.length; i++) {
                    if (text[i] === '[') {
                      if (jsonStart === -1) jsonStart = i;
                      bracketCount++;
                    } else if (text[i] === ']') {
                      bracketCount--;
                      if (bracketCount === 0 && jsonStart !== -1) {
                        jsonEnd = i;
                        break;
                      }
                    }
                  }
                  if (jsonStart !== -1 && jsonEnd !== -1) {
                    const jsonStr = text.substring(jsonStart, jsonEnd + 1);
                    const fullMatch = text.substring(idx1, jsonEnd + (text.endsWith(']]') ? 2 : 1));
                    return { json: jsonStr, fullMatch: fullMatch };
                  }
                }
                return null;
              };
              
              const suggestionsData = extractSuggestions(cleanedResponse);
              let parsedSuggestions = null;
              if (suggestionsData) {
                try {
                  parsedSuggestions = JSON.parse(suggestionsData.json);
                  cleanedResponse = cleanedResponse.replace(suggestionsData.fullMatch, '').trim();
                } catch (e) {
                  cleanedResponse = cleanedResponse.replace(suggestionsData.fullMatch, '').trim();
                }
              }
              cleanedResponse = sanitizeRawAnswer(cleanedResponse);
              
              newMessages.push({
                id: `history_${conv.id}_a`,
                type: 'ai',
                content: cleanedResponse,
                timestamp: new Date(conv.created_at),
                suggestions: parsedSuggestions || null,
                is_manual: conv.is_manual || 0
              });
            }
          });
          
          // Mevcut mesajlarla karşılaştır, yeni olanları ekle (içerik ve zaman bazlı duplicate kontrolü)
          setMessages(prev => {
            const toAdd = newMessages.filter(newMsg => {
              // Aynı içerikli ve yakın zamanda eklenmiş mesaj var mı kontrol et
              const isDuplicate = prev.some(existingMsg => {
                const sameContent = existingMsg.content === newMsg.content;
                const sameType = existingMsg.type === newMsg.type;
                const timeDiff = Math.abs(new Date(existingMsg.timestamp).getTime() - new Date(newMsg.timestamp).getTime());
                return sameContent && sameType && timeDiff < 10000; // 10 saniye içinde
              });
              return !isDuplicate;
            });
            
            if (toAdd.length > 0) {
              // Yeni mesajlar var, ekle
              return [...prev, ...toAdd].sort((a, b) => a.timestamp - b.timestamp);
            }
            return prev;
          });
          
          // Konuşmanın tamamlanıp tamamlanmadığını kontrol et
          if (data.is_ended) {
            setIsConversationEnded(true);
          }
          
          // Admin typing durumunu kontrol et
          if (data.admin_typing !== undefined) {
            setAdminTyping(data.admin_typing);
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 3000); // Her 3 saniyede bir kontrol et
    
    return () => clearInterval(pollInterval);
  }, [isOpen, sessionId, isConversationEnded, lastMessageSentAt]);

  const parseJsonResponse = async (response) => {
    const rawText = await response.text();
    const sanitizedText = rawText.replace(/^\uFEFF/, '').trim();
    if (!sanitizedText) {
      return {};
    }

    try {
      return JSON.parse(sanitizedText);
    } catch (error) {
      console.error('GogiAI JSON parse error:', error, sanitizedText);
      return { error: 'invalid_json', raw: sanitizedText };
    }
  };

  const stripNoise = (text) => {
    const noiseMarkers = [
      '### Satın Alma:',
      'Herhangi bir sorunuz',
      'Kaynak linkleri:',
      'Benzer konular:',
      'Kaynak:',
      'İyi günler dilerim',
      '🛒 Harika! Aşağıdaki butona tıklayarak'
    ];

    let cleaned = text;
    for (const marker of noiseMarkers) {
      const idx = cleaned.indexOf(marker);
      if (idx !== -1) {
        cleaned = cleaned.slice(0, idx).trim();
        break;
      }
    }

    return cleaned;
  };

  const removeDuplicateLines = (text) => {
    const lines = text.split('\n');
    const cleanedLines = [];
    let prevLine = null;
    for (let line of lines) {
      const trimmed = line.trim();
      if (trimmed === '' && prevLine === '') {
        continue;
      }
      if (trimmed && trimmed === prevLine) {
        continue;
      }
      cleanedLines.push(line);
      prevLine = trimmed || '';
    }
    return cleanedLines.join('\n').trim();
  };

  const sanitizeRawAnswer = (text) => {
    if (!text || typeof text !== 'string') return text;
    const idx = text.indexOf('\n\n**Kaynaklar:**');
    let cleaned = idx !== -1 ? text.slice(0, idx) : text;
    const idx2 = cleaned.indexOf('\n\nKaynaklar:');
    cleaned = idx2 !== -1 ? cleaned.slice(0, idx2) : cleaned;
    const buttonHtmls = [];
    cleaned = cleaned.replace(/\[BUTTON:([^\|]+)\|([^\|]+)\|([^\]]+)\]/g, (match, type, data, label) => {
      const btnClass = type === 'create-contact-request'
        ? 'mt-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium'
        : 'mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium';
      const attrs = type === 'create-contact-request'
        ? `data-button-type="${type}" data-button-data='${data}' data-button-label="${label}"`
        : `data-button-type="${type}" data-button-url="${data}" data-button-label="${label}"`;
      const btnHtml = `<button ${attrs} class="${btnClass}">${label}</button>`;
      buttonHtmls.push(btnHtml);
      return '';
    });
    cleaned = cleaned.replace(/\[([^\]]+)\]\(([^\)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">$1</a>');
    cleaned = cleaned.replace(/\s+$/g, '').replace(/\n{3,}/g, '\n\n');
    const base = removeDuplicateLines(stripNoise(cleaned.trim()));
    if (buttonHtmls.length === 0) {
      return base;
    }
    return [base, ...buttonHtmls].filter(Boolean).join('\n\n').trim();
  };

  // Müşteri bilgilerini yükle
  const loadCustomerInfo = async (sessionId) => {
    try {
      const apiBase = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : `${window.location.protocol}//${window.location.host}`;
      const response = await fetch(`${apiBase}/backend/api.php?action=gogiai_get_customer&session_id=${sessionId}`);
      const data = await parseJsonResponse(response);
      
      if (data.status === 'success' && data.data) {
        setCustomerInfo(data.data);
        // Müşteri bilgisi varsa karşılama ekranını göster
        setShowWelcomeScreen(true);
      } else {
        // Veritabanında yoksa localStorage kontrol et
        const lsFullName = localStorage.getItem('gogiai_customer_full_name');
        const lsGender = localStorage.getItem('gogiai_customer_gender');
        const lsPhone = localStorage.getItem('gogiai_customer_phone');
        const lsEmail = localStorage.getItem('gogiai_customer_email');
        const lsSector = localStorage.getItem('gogiai_customer_sector');
        if (lsFullName) {
          setCustomerInfo({ full_name: lsFullName, gender: lsGender, phone: lsPhone, email: lsEmail, sector: lsSector });
          setShowCustomerForm(false);
          setShowWelcomeScreen(true);
        } else {
          // Müşteri bilgisi yoksa form göster
          setShowCustomerForm(true);
        }
      }
    } catch (error) {
      console.error('Customer info load error:', error);
      // Hata durumunda da form göster
      setShowCustomerForm(true);
    }
  };

  // Widget açıldığında müşteri formunu göster (sadece müşteri bilgisi yoksa ve cookie kabul edilmişse)
  useEffect(() => {
    const lsFullName = localStorage.getItem('gogiai_customer_full_name');
    if (isOpen && !customerInfo && !lsFullName && cookieAccepted) {
      // Kısa bir gecikme ile form göster (widget animasyonu tamamlandıktan sonra)
      const timer = setTimeout(() => {
        setShowCustomerForm(true);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, customerInfo, cookieAccepted]);

  // Konuşma geçmişini yükle
  const loadConversationHistory = async (sessionId) => {
    try {
      const apiBase = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : `${window.location.protocol}//${window.location.host}`;
      const response = await fetch(`${apiBase}/backend/api.php?action=gogiai_conversation_history`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, limit: 20 })
      });
      
      const data = await parseJsonResponse(response);
      // Konuşmanın tamamlanıp tamamlanmadığını kontrol et
      if (data.is_ended) {
        setIsConversationEnded(true);
      }
      if (data.conversations && data.conversations.length > 0) {
        // Zaman damgasına göre artan sıraya getir
        const rows = [...data.conversations].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        // Her kayıt için önce kullanıcı, sonra AI mesajını sırayla ekle
        const historyMessages = [];
        rows.forEach((conv) => {
          if (conv.user_message) {
            historyMessages.push({
              id: `history_${conv.id}_u`,
              type: 'user',
              content: conv.user_message,
              timestamp: new Date(conv.created_at)
            });
          }
          if (conv.bot_response) {
            // Bot yanıtını sanitize et (buton formatını parse et)
            let cleanedResponse = conv.bot_response;
            // Suggestions'ı parse et
            let parsedSuggestions = null;
            // Suggestions formatı: [SUGGESTIONS:JSON_DATA] veya SUGGESTIONS:[JSON_DATA]
            // JSON içinde köşeli parantez olabilir, bu yüzden balanced bracket matching kullan
            const extractSuggestions = (text) => {
              // Önce [SUGGESTIONS:...] formatını dene (backend'den gelen format)
              const idx1 = text.lastIndexOf('[SUGGESTIONS:');
              if (idx1 !== -1) {
                const start = idx1 + '[SUGGESTIONS:'.length;
                // Balanced bracket matching: [ sayısını takip et
                let bracketCount = 0;
                let jsonStart = -1;
                let jsonEnd = -1;
                for (let i = start; i < text.length; i++) {
                  if (text[i] === '[') {
                    if (jsonStart === -1) jsonStart = i;
                    bracketCount++;
                  } else if (text[i] === ']') {
                    bracketCount--;
                    if (bracketCount === 0 && jsonStart !== -1) {
                      jsonEnd = i;
                      break;
                    }
                  }
                }
                if (jsonStart !== -1 && jsonEnd !== -1) {
                  const jsonStr = text.substring(jsonStart, jsonEnd + 1);
                  const fullMatch = text.substring(idx1, jsonEnd + (text.endsWith(']]') ? 2 : 1));
                  return { json: jsonStr, fullMatch: fullMatch };
                }
              }
              
              // Sonra SUGGESTIONS:[...] formatını dene (alternatif format)
              const idx2 = text.lastIndexOf('SUGGESTIONS:');
              if (idx2 !== -1) {
                const start = idx2 + 'SUGGESTIONS:'.length;
                // Balanced bracket matching: [ sayısını takip et
                let bracketCount = 0;
                let jsonStart = -1;
                let jsonEnd = -1;
                for (let i = start; i < text.length; i++) {
                  if (text[i] === '[') {
                    if (jsonStart === -1) jsonStart = i;
                    bracketCount++;
                  } else if (text[i] === ']') {
                    bracketCount--;
                    if (bracketCount === 0 && jsonStart !== -1) {
                      jsonEnd = i;
                      break;
                    }
                  }
                }
                if (jsonStart !== -1 && jsonEnd !== -1) {
                  const jsonStr = text.substring(jsonStart, jsonEnd + 1);
                  const fullMatch = text.substring(idx2, jsonEnd + (text.endsWith(']]') ? 2 : 1));
                  return { json: jsonStr, fullMatch: fullMatch };
                }
              }
              
              return null;
            };
            
            const suggestionsData = extractSuggestions(cleanedResponse);
            if (suggestionsData) {
              try {
                parsedSuggestions = JSON.parse(suggestionsData.json);
                // Suggestions'ı answer'dan kaldır
                cleanedResponse = cleanedResponse.replace(suggestionsData.fullMatch, '').trim();
              } catch (e) {
                console.error('Suggestions parse error in history:', e, suggestionsData.json);
                // Parse edilemezse suggestions'ı yine de kaldır
                cleanedResponse = cleanedResponse.replace(suggestionsData.fullMatch, '').trim();
                parsedSuggestions = null;
              }
            }
            cleanedResponse = sanitizeRawAnswer(cleanedResponse);
            historyMessages.push({
              id: `history_${conv.id}_a`,
              type: 'ai',
              content: cleanedResponse,
              timestamp: new Date(conv.created_at),
              suggestions: parsedSuggestions || null,
              is_manual: conv.is_manual || 0 // Manuel mesaj işareti
            });
          }
        });
        setMessages(historyMessages);
      } else {
        setMessages([]);
      }
    } catch (error) {
      console.error('Konuşma geçmişi yükleme hatası:', error);
    }
  };

  // Yeni konuşma başlat
  const startNewConversation = async () => {
    const oldSessionId = sessionId || localStorage.getItem('gogiai_session_id');
    
    // Önceki konuşmayı tamamlandı olarak işaretle
    if (oldSessionId) {
      try {
        const apiBase = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : `${window.location.protocol}//${window.location.host}`;
        const response = await fetch(`${apiBase}/backend/api.php?action=gogiai_end_conversation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: oldSessionId })
        });
        const data = await response.json();
        if (!data.success) {
          console.error('Failed to end conversation:', data);
        }
      } catch (error) {
        console.error('Error ending previous conversation:', error);
      }
    }
    
    const newSessionId = generateSessionId();
    setSessionId(newSessionId);
    localStorage.setItem('gogiai_session_id', newSessionId);
    
    // Müşteri bilgilerini temizle (yeni konuşma için)
    setCustomerInfo(null);
    setIsConversationEnded(false); // Yeni konuşma başladığında tamamlandı durumunu sıfırla
    
    // Eğer localStorage'da müşteri bilgileri varsa, yeni session_id ile kaydet
    const lsFullName = localStorage.getItem('gogiai_customer_full_name');
    const lsGender = localStorage.getItem('gogiai_customer_gender');
    const lsPhone = localStorage.getItem('gogiai_customer_phone');
    const lsEmail = localStorage.getItem('gogiai_customer_email');
    const lsSector = localStorage.getItem('gogiai_customer_sector');
    
    if (lsFullName) {
      // Müşteri bilgilerini yeni session_id ile kaydet
      const customerData = {
        full_name: lsFullName,
        gender: lsGender || null,
        phone: lsPhone || '',
        email: lsEmail || '',
        sector: lsSector || ''
      };
      
      // Yeni session_id ile kaydet (sessionId state güncellenene kadar newSessionId kullan)
      const saved = await saveCustomerInfo(customerData, newSessionId);
      if (saved) {
        setCustomerInfo(customerData);
        setShowCustomerForm(false);
        setShowWelcomeScreen(true);
      } else {
        // Kayıt başarısız olursa form göster
        setShowCustomerForm(true);
        setShowWelcomeScreen(false);
      }
    } else {
      // Müşteri bilgisi yoksa form göster
      setShowCustomerForm(true);
      setShowWelcomeScreen(false);
    }
    
    setMessages([]);
  };

  // Müşteri bilgilerini kaydet (sessionId parametresi ile)
  const saveCustomerInfo = async (customerData, customSessionId = null) => {
    try {
      const apiBase = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : `${window.location.protocol}//${window.location.host}`;
      
      // Gönderilecek veriyi hazırla
      const payload = {
        session_id: customSessionId || sessionId,
        full_name: customerData.full_name,
        phone: customerData.phone,
        email: customerData.email,
        sector: customerData.sector,
        gender: customerData.gender || null
      };
      
      const response = await fetch(`${apiBase}/backend/api.php?action=gogiai_save_customer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      const data = await parseJsonResponse(response);
      
      if (data.status === 'success') {
        setCustomerInfo(customerData);
        setShowCustomerForm(false);
        // Başarılı kayıt sonrası karşılama ekranını göster
        setShowWelcomeScreen(true);
        setMessages([]);
        // LocalStorage'a da yaz (yenilemeler için kalıcılık)
        try {
          localStorage.setItem('gogiai_customer_full_name', customerData.full_name);
          if (customerData.gender) localStorage.setItem('gogiai_customer_gender', customerData.gender);
          localStorage.setItem('gogiai_customer_phone', customerData.phone || '');
          localStorage.setItem('gogiai_customer_email', customerData.email || '');
          localStorage.setItem('gogiai_customer_sector', customerData.sector || '');
        } catch (_) {}
        return true;
      } else {
        console.error('Backend returned error:', data);
        return false;
      }
    } catch (error) {
      console.error('Customer save error:', error);
      return false;
    }
  };

  // Cookie kabul et - Kaldırıldı

  const handleSendMessage = async (messageText = newMessage) => {
    if (!messageText.trim()) return;
    
    // Konuşma tamamlandıysa mesaj gönderme
    if (isConversationEnded) {
      alert('Bu konuşma tamamlandı. Yeni bir konuşma başlatmak için "Yeni Sohbet" butonuna tıklayın.');
      return;
    }

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: messageText,
      timestamp: new Date()
    };

    // Duplicate kontrolü: Aynı içerikli kullanıcı mesajı zaten varsa ekleme
    setMessages(prev => {
      const existingMessage = prev.find(m => 
        m.type === 'user' && 
        m.content === messageText &&
        Math.abs(new Date(m.timestamp).getTime() - Date.now()) < 5000 // 5 saniye içinde
      );
      if (existingMessage) {
        return prev; // Duplicate, ekleme
      }
      return [...prev, userMessage];
    });
    
    // Mesaj gönderildiğinde timestamp'i kaydet (polling'i durdurmak için)
    setLastMessageSentAt(Date.now());
    
    setNewMessage('');
    setIsTyping(true);
    setShowWelcomeScreen(false); // İlk mesaj gönderildiğinde karşılama ekranını gizle

    try {
      // Gerçek AI API çağrısı
      const apiBase = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : `${window.location.protocol}//${window.location.host}`;
      
      // Timeout ile API çağrısı
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 saniye timeout (paket bilgileri için daha uzun süre gerekebilir)
      
      const activeSessionId = sessionId || localStorage.getItem('gogiai_session_id');
      const payload = {
        question: messageText,
        session_id: activeSessionId
      };
      
      // Müşteri bilgilerini payload'a ekle (state'ten veya localStorage'dan)
      const currentCustomerInfo = customerInfo || {
        sector: localStorage.getItem('gogiai_customer_sector'),
        full_name: localStorage.getItem('gogiai_customer_full_name'),
        phone: localStorage.getItem('gogiai_customer_phone'),
        email: localStorage.getItem('gogiai_customer_email'),
        gender: localStorage.getItem('gogiai_customer_gender')
      };
      
      // Sektör bilgisi varsa payload'a ekle (sadece sector kontrolü yeterli değil, tüm bilgileri gönder)
      if (currentCustomerInfo && (currentCustomerInfo.sector || currentCustomerInfo.full_name || currentCustomerInfo.phone || currentCustomerInfo.email)) {
        payload.customer_info = {
          sector: currentCustomerInfo.sector || null,
          full_name: currentCustomerInfo.full_name || null,
          phone: currentCustomerInfo.phone || null,
          email: currentCustomerInfo.email || null,
          gender: currentCustomerInfo.gender || null
        };
      }

      const response = await fetch(`${apiBase}/backend/api.php?action=gogiai_chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      const data = await parseJsonResponse(response);
      
      if (data.error) {
        throw new Error(`API invalid JSON response: ${data.raw || data.error}`);
      }

      // Eğer silent flag varsa, mesaj gösterme (konuşma devralındı, sadece kullanıcı mesajı kaydedildi)
      if (data.silent) {
        setIsTyping(false);
        return; // Mesaj gösterme, sadece kullanıcı mesajı kaydedildi
      }
      
      if (data.answer) {
        const cleanedAnswer = sanitizeRawAnswer(data.answer);
        // Suggestions'ı parse et
        let parsedSuggestions = null;
        
        // Suggestions formatı: [SUGGESTIONS:JSON_DATA] veya SUGGESTIONS:[JSON_DATA]
        // JSON içinde köşeli parantez olabilir, bu yüzden balanced bracket matching kullan
        const extractSuggestions = (text) => {
          // Önce [SUGGESTIONS:...] formatını dene (backend'den gelen format)
          const idx1 = text.lastIndexOf('[SUGGESTIONS:');
          if (idx1 !== -1) {
            const start = idx1 + '[SUGGESTIONS:'.length;
            // Balanced bracket matching: [ sayısını takip et
            let bracketCount = 0;
            let jsonStart = -1;
            let jsonEnd = -1;
            for (let i = start; i < text.length; i++) {
              if (text[i] === '[') {
                if (jsonStart === -1) jsonStart = i;
                bracketCount++;
              } else if (text[i] === ']') {
                bracketCount--;
                if (bracketCount === 0 && jsonStart !== -1) {
                  jsonEnd = i;
                  break;
                }
              }
            }
            if (jsonStart !== -1 && jsonEnd !== -1) {
              const jsonStr = text.substring(jsonStart, jsonEnd + 1);
              const fullMatch = text.substring(idx1, jsonEnd + (text.endsWith(']]') ? 2 : 1));
              return { json: jsonStr, fullMatch: fullMatch };
            }
          }
          
          // Sonra SUGGESTIONS:[...] formatını dene (alternatif format)
          const idx2 = text.lastIndexOf('SUGGESTIONS:');
          if (idx2 !== -1) {
            const start = idx2 + 'SUGGESTIONS:'.length;
            // Balanced bracket matching: [ sayısını takip et
            let bracketCount = 0;
            let jsonStart = -1;
            let jsonEnd = -1;
            for (let i = start; i < text.length; i++) {
              if (text[i] === '[') {
                if (jsonStart === -1) jsonStart = i;
                bracketCount++;
              } else if (text[i] === ']') {
                bracketCount--;
                if (bracketCount === 0 && jsonStart !== -1) {
                  jsonEnd = i;
                  break;
                }
              }
            }
            if (jsonStart !== -1 && jsonEnd !== -1) {
              const jsonStr = text.substring(jsonStart, jsonEnd + 1);
              const fullMatch = text.substring(idx2, jsonEnd + (text.endsWith(']]') ? 2 : 1));
              return { json: jsonStr, fullMatch: fullMatch };
            }
          }
          
          return null;
        };
        
        const suggestionsData = extractSuggestions(cleanedAnswer);
        if (suggestionsData) {
          try {
            parsedSuggestions = JSON.parse(suggestionsData.json);
            // Suggestions'ı answer'dan kaldır
            cleanedAnswer = cleanedAnswer.replace(suggestionsData.fullMatch, '').trim();
          } catch (e) {
            console.error('Suggestions parse error:', e, suggestionsData.json);
            // Parse edilemezse suggestions'ı yine de kaldır
            cleanedAnswer = cleanedAnswer.replace(suggestionsData.fullMatch, '').trim();
          }
        }
        
        
        // Session ID'yi kaydet
        if (data.session_id && data.session_id !== sessionId) {
          setSessionId(data.session_id);
          localStorage.setItem('gogiai_session_id', data.session_id);
        }
        
        const aiMessage = {
          id: Date.now() + 1,
          type: 'ai',
          content: cleanedAnswer,
          timestamp: new Date(),
          suggestions: parsedSuggestions || ['Başka soru sor', 'Detaylı bilgi', 'İletişime geç', 'Fiyat öğren'],
          is_manual: data.taken_over ? 1 : 0 // Manuel mesaj işareti
        };
        
        // Mesaj gönderildiğinde timestamp'i kaydet (polling'i durdurmak için)
        setLastMessageSentAt(Date.now());
        
        // Duplicate kontrolü: Aynı içerikli mesaj zaten varsa ekleme
        setMessages(prev => {
          const existingMessage = prev.find(m => 
            m.type === 'ai' && 
            m.content === cleanedAnswer &&
            Math.abs(new Date(m.timestamp).getTime() - Date.now()) < 10000 // 10 saniye içinde
          );
          if (existingMessage) {
            return prev; // Duplicate, ekleme
          }
          return [...prev, aiMessage];
        });
        setIsTyping(false);
        
        // Mesaj widget'a yazıldıktan sonra bildirimleri gönder (arka planda)
        if (data.notify_after_display) {
          const currentSessionId = sessionId || localStorage.getItem('gogiai_session_id');
          if (currentSessionId && messageText) {
            // Arka planda bildirimleri tetikle (await kullanmadan, fire-and-forget)
            const apiBaseUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : `${window.location.protocol}//${window.location.host}`;
            fetch(`${apiBaseUrl}/backend/api.php?action=gogiai_send_notifications`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                session_id: currentSessionId,
                user_message: messageText
              })
            }).catch(err => {
              // Hata olsa bile kullanıcıya gösterme, sadece log
              console.error('Bildirim gönderme hatası:', err);
            });
          }
        }
      } else {
        throw new Error(data.error || data.message || 'API hatası');
      }
    } catch (error) {
      console.error('GogiAI API hatası:', error);
      
      // Timeout hatası için özel mesaj
      if (error.name === 'AbortError') {
        const timeoutMessage = {
          id: Date.now() + 1,
          type: 'ai',
          content: 'Üzgünüm, yanıt vermem biraz zaman aldı. Lütfen sorunuzu tekrar sorun veya daha kısa bir soru deneyin.',
          timestamp: new Date(),
          suggestions: ['Tekrar dene', 'Kısa soru sor', 'Başka konu', 'İletişime geç']
        };
        setMessages(prev => [...prev, timeoutMessage]);
        setIsTyping(false);
        return;
      }
      
      // Fallback: Basit yanıtlar
      const getFallbackDefault = () => {
        const savedSector = customerInfo?.sector || localStorage.getItem('gogiai_customer_sector');
        if (savedSector) {
          return {
            content: `Anlıyorum! ${savedSector} sektöründe olduğunuzu biliyorum. ✨\n\nSize en iyi hizmeti sunabilmek için lütfen diğer detayları paylaşın:\n\n• Bütçeniz ne kadar?\n• Ne zaman başlamak istiyorsunuz?\n• Özel gereksinimleriniz var mı?`,
            suggestions: ['Bütçe belirt', 'Zaman planı', 'Özel gereksinim', 'Demo talep et']
          };
        }
        return {
          content: 'Anlıyorum! Bu konuda size yardımcı olmaya hazırım. ✨\n\nSize en iyi hizmeti sunabilmek için lütfen daha detay verin:\n\n• Hangi sektördesiniz?\n• Bütçeniz ne kadar?\n• Ne zaman başlamak istiyorsunuz?\n• Özel gereksinimleriniz var mı?',
          suggestions: ['Sektör bilgisi ver', 'Bütçe belirt', 'Zaman planı', 'Özel gereksinim']
        };
      };

      const designPackageSummaries = [
        { name: 'Kurumsal', desc: 'Profesyonel, güven veren kurumsal web siteleri.' },
        { name: 'Kolay Site', desc: 'Hazır şablonlar ve sürükle-bırak ile hızlı kurulum.' },
        { name: 'E-Ticaret', desc: 'Katalog, ödeme ve kargo entegrasyonlu online mağazalar.' }
      ];

      const buildDesignFallbackResponse = () => {
        const savedSector = customerInfo?.sector || localStorage.getItem('gogiai_customer_sector');
        const intro = savedSector
          ? `Anlıyorum! ${savedSector} sektöründe olduğunuzu biliyorum. ✨`
          : 'Web tasarım paketlerimiz hakkında bilgi vermekten memnuniyet duyarım! ✨';

        const lines = designPackageSummaries.map(item => `- **${item.name}**: ${item.desc}`);
        const content = `${intro}\n\nWeb tasarım paketlerimiz şu şekilde ayrılıyor:\n${lines.join('\n')}\n\nHangi kategoriyle devam etmemi istersiniz?`;
        const suggestions = designPackageSummaries.map(item => `${item.name} paketleri`);
        suggestions.push('Detaylı ihtiyaç paylaş');
        return { content, suggestions };
      };

      const isDesignQuestion = (text) => {
        const designKeywords = ['web tasarım', 'web tasarim', 'tasarım paketi', 'tasarim paketi', 'tasarım paketleri', 'tasarim paketleri', 'tasarım', 'tasarim'];
        const hasPackageWord = text.includes('paket');
        const hitsKeyword = designKeywords.some(keyword => text.includes(keyword));
        return hasPackageWord && hitsKeyword;
      };

      const fallbackResponses = {
        'paket öner': {
          content: 'Harika! Size en uygun paketi önerebilirim. İşletmenizin büyüklüğü nedir? 📊\n\n• Küçük işletme (0-10 çalışan)\n• Orta ölçekli (10-50 çalışan) \n• Büyük şirket (50+ çalışan)',
          suggestions: ['Küçük işletme', 'Orta ölçekli', 'Büyük şirket', 'Detaylı analiz iste']
        },
        'e-ticaret': {
          content: 'E-ticaret için 8 farklı paketimiz var! 🛒\n\n**Basic Paketler (4 adet):**\n• Başlangıç: ₺299/ay \n• Büyüme: ₺599/ay\n• Ölçekle: ₺999/ay\n• Kurumsal: ₺1999/ay\n\n**Professional B2B (4 adet):**\n• B2B Starter: ₺1499/ay\n• B2B Growth: ₺2999/ay\n• B2B Enterprise: ₺4999/ay\n• B2B Ultimate: ₺9999/ay',
          suggestions: ['Basic paketler', 'B2B paketler', 'Paket karşılaştır', 'Demo talep et']
        },
        'fiyat hesapla': {
          content: 'Proje maliyetinizi hesaplayalım! 💰 Hangi hizmetlere ihtiyacınız var?\n\n• Web sitesi: ₺5000-25000\n• Mobil app: ₺15000-50000\n• E-ticaret: ₺8000-35000\n• Logo tasarım: ₺500-3000\n• SEO: ₺2000-8000\n\nDetaylı hesaplama için proje detaylarınızı paylaşın!',
          suggestions: ['Web sitesi', 'Mobil uygulama', 'E-ticaret sitesi', 'Özel teklif al']
        },
        'freelancer': {
          content: 'En uygun freelancerı bulmanıza yardımcı olayım! 👥\n\nHangi alanda uzman arıyorsunuz?\n\n• Web Geliştirme (850+ uzman)\n• Tasarım (620+ uzman)\n• Pazarlama (340+ uzman)\n• İçerik (480+ uzman)\n\nTüm freelancerlarımız doğrulanmış ve puanlanmış!',
          suggestions: ['Web developer', 'UI/UX designer', 'SEO uzmanı', 'İçerik yazarı']
        },
        'hosting': {
          content: 'Size perfect hosting paketini bulalım! 🌐\n\n**Hosting Seçenekleri:**\n• Paylaşımlı Hosting: ₺99-199/ay\n• VPS Hosting: ₺499/ay\n• Dedicated Server: ₺1999/ay\n\nWeb sitenizin trafiği ve ihtiyaçları nedir?',
          suggestions: ['Küçük site', 'Orta trafik', 'Yüksek trafik', 'E-ticaret sitesi']
        },
        'demo': {
          content: 'Harika! Demo için hemen iletişim bilgilerinizi alalım! 🚀\n\nSize özel demo sunumu hazırlayabilirim:\n\n• Platform turu (15 dk)\n• Canlı paket gösterimi\n• Soru-cevap\n• Özel fiyat teklifi\n\nHangi tarih size uygun?',
          suggestions: ['Bu hafta', 'Gelecek hafta', 'Esnek zamanlı', 'Telefon bilgisi ver']
        },
        'irc': {
          content: 'IRC chat sistemimiz profesyoneller için özel! 💬\n\n**Premium IRC Erişimi (₺399/ay):**\n• Freelancer kanalları\n• Ajans networking\n• Bayi grupları\n• İş fırsatları\n• 7/24 aktif topluluk\n\nHangi kategoride networking yapmak istiyorsunuz?',
          suggestions: ['Freelancer grupları', 'Ajans network', 'Bayi kanalları', 'Üyelik al']
        },
        'whatsapp': {
          content: 'WhatsApp CRM sistemimizle müşteri ilişkilerinizi yönetin! 📱\n\n**Özellikler:**\n• Otomatik mesaj yanıtlama\n• Müşteri takip sistemi\n• Satış pipeline yönetimi\n• Raporlama ve analytics\n\nWhatsApp entegrasyonu hakkında detay ister misiniz?',
          suggestions: ['CRM özellikleri', 'Fiyatlandırma', 'Kurulum', 'Demo talep et']
        }
      };

      const lowerMessage = messageText.toLowerCase();
      let response = isDesignQuestion(lowerMessage) ? buildDesignFallbackResponse() : getFallbackDefault();
      
      // Smart response matching
      if (!isDesignQuestion(lowerMessage)) {
        for (let key in fallbackResponses) {
          if (lowerMessage.includes(key) || 
              lowerMessage.includes(key.replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ş/g, 's').replace(/ü/g, 'u'))) {
            response = fallbackResponses[key];
            break;
          }
        }
      }

      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: response.content,
        timestamp: new Date(),
        suggestions: response.suggestions
      };

      setMessages(prev => [...prev, aiMessage]);
      setIsTyping(false);
    }
    finally {
      // Güvenlik: herhangi bir durumda typing kapatılsın
      setIsTyping(false);
    }
  };

  const handleQuickAction = (action) => {
    handleSendMessage(action);
  };

  const handleSuggestionClick = (suggestion) => {
    // Eğer suggestion bir obje ise (type ve action içeriyorsa)
    if (typeof suggestion === 'object' && suggestion !== null) {
      if (suggestion.type === 'link' && suggestion.url) {
        // Link butonu - sayfaya yönlendir
        window.location.href = suggestion.url;
      } else if (suggestion.action) {
        // Soru butonu - action'ı gönder
        handleSendMessage(suggestion.action);
      } else if (suggestion.text) {
        // Fallback - text'i gönder
        handleSendMessage(suggestion.text);
      }
    } else {
      // String ise direkt gönder
      handleSendMessage(suggestion);
    }
  };

  // Geçmiş konuşmalar gösterimi kaldırıldı

  if (!isOpen) {
    return (
      <div className="fixed right-[5%] md:right-6 z-40 md:z-50" style={{bottom: '2.5rem'}}>
        <div className="relative">
          {/* Multiple pulse rings for more attention - slowed down */}
          <div className="absolute inset-0 bg-[#7A5AFF]/60 rounded-full animate-ping" style={{animationDuration: '2s'}}></div>
          <div className="absolute inset-0 bg-[#7A5AFF] rounded-full animate-pulse" style={{animationDuration: '2s'}}></div>
          <div className="absolute inset-0 bg-[#7A5AFF]/40 rounded-full animate-ping" style={{animationDelay: '1s', animationDuration: '2s'}}></div>
          
          {/* Main Button */}
          <Button
            onClick={() => setIsOpen(true)}
            className="relative w-20 h-20 rounded-full bg-gradient-to-r from-[#7A5AFF] to-[#5a3fd9] hover:from-[#8b6bff] hover:to-[#6b4de6] shadow-2xl shadow-[#7A5AFF]/40 transform hover:scale-110 transition-all duration-300 group"
          >
            <Bot className="w-10 h-10 text-white animate-bounce" />
          </Button>
          
          {/* Enhanced Tooltip */}
          <div className="absolute bottom-full right-0 mb-4 px-6 py-3 bg-gray-900 text-white text-sm rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap shadow-xl pointer-events-none z-[40]">
            <div className="flex items-center space-x-2">
              <Sparkles className="w-4 h-4 text-yellow-400" />
              <span className="font-semibold">Gogi AI ile sohbet et!</span>
              <Bot className="w-4 h-4" />
            </div>
            <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
          </div>
        </div>
      </div>
    );
  }

  // Widget boyutunu içerik durumuna göre belirle
  const getWidgetSize = () => {
    if (isMinimized) {
      return 'w-[calc(100vw-1.5rem)] md:w-80 h-16';
    }
    
    // Cookie kabul edilmemişse küçük boyut
    if (!cookieAccepted) {
      return 'w-[calc(100vw-1.5rem)] md:w-[380px] h-[180px]';
    }
    
    // Müşteri formu gösteriliyorsa orta boyut
    if (showCustomerForm) {
      return 'w-[calc(100vw-1.5rem)] md:w-[420px] h-[450px]';
    }
    
    // Normal sohbet modu - sabit boyut (konuşmalar artınca büyümez)
    return 'w-[calc(100vw-1.5rem)] md:w-[420px] h-[600px]';
  };

  return (
    <div className="fixed right-[3%] md:right-6 z-40 md:z-50" style={{bottom: '2.5rem'}}>
      <Card className={`shadow-2xl border-0 transition-all duration-300 ${getWidgetSize()}`}>
        {/* Enhanced Header */}
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-black via-[#1a0a2e] to-[#7A5AFF] text-white rounded-t-lg">
          <div className="flex items-center space-x-3">
            <div className="relative">
              <Avatar className="h-12 w-12 ring-2 ring-white/50">
                <AvatarFallback className="bg-gradient-to-br from-[#7A5AFF] to-[#5a3fd9] text-white font-bold text-xl flex items-center justify-center">
                  <Bot className="w-6 h-6" />
                </AvatarFallback>
              </Avatar>
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 border-2 border-white rounded-full animate-pulse"></div>
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="font-bold text-lg">Gogi AI</h3>
              </div>
              <p className="text-xs text-white/90">Website Al Yapay Zeka Asistanı • Online</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={startNewConversation}
              className="h-8 w-8 text-white hover:bg-white/20"
              title="Yeni Sohbet Başlat"
            >
              <MessageSquare className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMinimized(!isMinimized)}
              className="h-8 w-8 text-white hover:bg-white/20"
            >
              {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(false)}
              className="h-8 w-8 text-white hover:bg-white/20"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {!isMinimized && (
          <>

            {/* Customer Form - Widget içerisinde */}
            {showCustomerForm && cookieAccepted && (
              <div className="p-3 bg-gradient-to-r from-[#7A5AFF]/5 to-[#7A5AFF]/10 border-b border-[#7A5AFF]/10">
                <div className="flex items-center justify-end mb-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowCustomerForm(false);
                      // Form kapatıldığında welcome screen'i göster ve bot'u kullanılabilir yap
                      setShowWelcomeScreen(true);
                    }}
                    className="h-5 w-5 p-0"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
                <CustomerForm onSave={saveCustomerInfo} />
              </div>
            )}

            {/* Category Tabs kaldırıldı */}

            {/* Messages - Sadece form tamamlandıktan sonra göster */}
            {!showCustomerForm && cookieAccepted && (
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50" style={{ 
              height: '400px', 
              maxHeight: '400px' 
            }}>
              {(messages.length === 0 && showWelcomeScreen) && (
                <div className="space-y-6">
                  {/* Welcome Message */}
                  <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-[#7A5AFF]/5 to-[#7A5AFF]/10 rounded-xl border border-[#7A5AFF]/20">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#7A5AFF] to-[#5a3fd9] rounded-full flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-7 h-7 text-white animate-pulse" />
                    </div>
                    <div className="flex-1">
                      <p className="text-base font-bold text-gray-900">
                        {customerInfo ? `Merhaba ${customerInfo.full_name.split(' ')[0].charAt(0).toUpperCase() + customerInfo.full_name.split(' ')[0].slice(1).toLowerCase()} ${customerInfo.gender === 'male' ? 'Bey' : (customerInfo.gender === 'female' ? 'Hanım' : '')}!` : 'Gogi AI ile tanışın!'}
                      </p>
                      <p className="text-sm text-gray-600 mt-0.5">Size nasıl yardımcı olabilirim?</p>
                    </div>
                  </div>

                  {/* Quick Actions Grid */}
                  <div className="grid grid-cols-2 gap-3">
                    {quickActions.map((action, idx) => {
                      const IconComponent = action.icon;
                      return (
                        <button
                          key={idx}
                          onClick={() => handleQuickAction(action.action)}
                          className="p-3 bg-white rounded-xl border border-gray-200 hover:border-[#7A5AFF]/40 hover:shadow-md transition-all duration-300 text-left group"
                        >
                          <div className="flex items-start space-x-2">
                            <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${action.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                              <IconComponent className="w-4 h-4 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-gray-900 text-xs leading-tight">{action.title}</h4>
                              <p className="text-xs text-gray-500 mt-1 leading-tight">{action.description}</p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Category Info kaldırıldı */}
                </div>
              )}

              {messages.filter(Boolean).map((message, index) => (
                <div
                  key={message?.id ?? index}
                  className={`flex ${message?.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-xs ${message?.type === 'user' ? 'order-2' : 'order-1'}`}>
                    {/* Gönderen adı (AI mesajları için) */}
                    {message?.type === 'ai' && (
                      <div className="mb-1 px-2">
                        <span className="text-xs font-semibold text-gray-600">
                          {message?.is_manual 
                            ? 'Website Al Müşteri Danışmanı' 
                            : 'Gogi AI'}
                        </span>
                      </div>
                    )}
                    <div className={`px-4 py-3 rounded-2xl text-sm ${
                      message?.type === 'user' 
                        ? 'bg-[#7A5AFF] text-white rounded-br-sm' 
                        : 'bg-white text-gray-900 rounded-bl-sm shadow-sm border border-gray-100'
                    }`}>
                      {(() => {
                        const escapeHtml = (s) => (s || '')
                          .replace(/&/g, '&amp;')
                          .replace(/</g, '&lt;')
                          .replace(/>/g, '&gt;')
                          .replace(/"/g, '&quot;')
                          .replace(/'/g, '&#039;');
                        // HTML linklerini koruyarak kalın yazıları işle
                        const toBoldHtml = (s) => {
                          if (!s) return '';
                          // Önce HTML linklerini geçici olarak sakla
                          const linkPlaceholders = [];
                          let processed = s.replace(/<a[^>]*>.*?<\/a>/gi, (match) => {
                            const placeholder = `__LINK_${linkPlaceholders.length}__`;
                            linkPlaceholders.push(match);
                            return placeholder;
                          });
                          // Markdown linklerini HTML'e çevir [text](url) -> <a href="url">text</a>
                          processed = processed.replace(/\[([^\]]+)\]\(([^\)]+)\)/g, (match, text, url) => {
                            const placeholder = `__LINK_${linkPlaceholders.length}__`;
                            linkPlaceholders.push(`<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">${text}</a>`);
                            return placeholder;
                          });
                          // Kalan metni escape et ve kalın yazıları işle
                          processed = escapeHtml(processed).replace(/\*\*(.+?)\*\*/g, '<strong>$1<\/strong>');
                          // Linkleri geri yerleştir
                          linkPlaceholders.forEach((link, index) => {
                            processed = processed.replace(`__LINK_${index}__`, link);
                          });
                          return processed;
                        };
                        const infoText = 'Detaylı bilgi almak isterseniz, web sitemizi ziyaret edebilirsiniz:';
                        const stripLinks = (s) => s
                          .replace(/\[[^\]]*\]\([^\)]*\)/g, '')
                          .replace(/https?:\/\/[^\s)]+/g, '')
                          .trim();
                        const text = message?.content ?? '';
                        if (message?.type === 'ai' && typeof text === 'string' && text.includes(infoText)) {
                          const parts = text.split(infoText);
                          const before = stripLinks(parts[0] || '');
                          const after = stripLinks(parts.slice(1).join(infoText) || '');
                          return (
                            <p className="leading-relaxed whitespace-pre-line">
                              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(toBoldHtml(before) || '') }} />
                              {before ? '\n\n' : ''}
                              {infoText} <button onClick={() => window.open('/contact', '_blank')} className="text-[#7A5AFF] hover:text-[#5a3fd9] underline font-medium align-baseline">İletişim</button>{after ? ' ' : ''}
                              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(toBoldHtml(after) || '') }} />
                            </p>
                          );
                        }
                        // Butonları parse et ve ayrı render et
                        // Önce create-contact-request butonlarını bul (JSON data içerebilir)
                        const contactRequestRegex = /<button[^>]*data-button-type="create-contact-request"[^>]*data-button-data='([^']*)'[^>]*data-button-label="([^"]*)"[^>]*>([^<]*)<\/button>/gi;
                        const buttons = [];
                        let processedText = text;
                        
                        // Müşteri bilgisi kontrolü
                        const hasCustomerInfo = customerInfo && (customerInfo.full_name || customerInfo.phone || customerInfo.email);
                        
                        // İletişim talebi butonlarını bul (sadece müşteri bilgisi varsa)
                        processedText = processedText.replace(contactRequestRegex, (match, data, label, content) => {
                          // Müşteri bilgisi varsa butonu ekle
                          if (hasCustomerInfo) {
                            buttons.push({ type: 'create-contact-request', data, label: label || content || 'İletişim Talebi Oluştur', content: content || label || 'İletişim Talebi Oluştur' });
                          }
                          // Buton HTML'ini kaldır (müşteri bilgisi yoksa da kaldır, gösterme)
                          return '';
                        });
                        
                        // Normal URL butonlarını bul
                        const urlButtonRegex = /<button[^>]*data-button-type="([^"]*)"[^>]*data-button-url="([^"]*)"[^>]*data-button-label="([^"]*)"[^>]*>([^<]*)<\/button>/gi;
                        processedText = processedText.replace(urlButtonRegex, (match, type, url, label, content) => {
                          // Label varsa onu kullan, yoksa content'i kullan
                          buttons.push({ type, url, label: label || content || 'Paketi Satın Al', content: content || label || 'Paketi Satın Al' });
                          return ''; // Buton HTML'ini kaldır
                        });
                        
                        // Emoji'leri ve boş satırları temizle (🛒, 💬 gibi)
                        processedText = processedText.replace(/^[🛒💬]\s*$/gm, '').replace(/\n{3,}/g, '\n\n').trim();
                        
                        return (
                          <div>
                            <p
                              className="leading-relaxed whitespace-pre-line"
                              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(toBoldHtml(processedText) || '') }}
                            />
                            {buttons.length > 0 && (
                              <div className="mt-3">
                                {buttons.map((button, idx) => (
                                  <button
                                    key={idx}
                                    onClick={async () => {
                                      if (button.type === 'create-contact-request') {
                                        // İletişim talebi oluştur
                                        try {
                                          const apiBase = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : `${window.location.protocol}//${window.location.host}`;
                                          const packageData = JSON.parse(button.data);
                                          
                                          const response = await fetch(`${apiBase}/backend/api.php?action=gogiai_create_contact_request`, {
                                            method: 'POST',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({
                                              session_id: sessionId || localStorage.getItem('gogiai_session_id'),
                                              package_data: packageData
                                            })
                                          });
                                          
                                          const result = await response.json();
                                          
                                          if (result.status === 'success') {
                                            // Başarı mesajı göster
                                            const successMessage = {
                                              id: Date.now() + 1,
                                              type: 'ai',
                                              content: result.message || '✅ İletişim talebiniz başarıyla oluşturuldu! En kısa sürede size dönüş yapacağız.',
                                              timestamp: new Date()
                                            };
                                            setMessages(prev => [...prev, successMessage]);
                                          } else {
                                            // Hata mesajını göster
                                            const errorMessage = result.message || 'Talep oluşturulurken bir hata oluştu. Lütfen önce bilgilerinizi paylaşın.';
                                            const errorMsg = {
                                              id: Date.now() + 1,
                                              type: 'ai',
                                              content: `❌ ${errorMessage}`,
                                              timestamp: new Date()
                                            };
                                            setMessages(prev => [...prev, errorMsg]);
                                            
                                            // Müşteri bilgisi yoksa formu göster
                                            if (result.message && result.message.includes('bilgilerinizi paylaşın')) {
                                              setShowCustomerForm(true);
                                            }
                                          }
                                        } catch (error) {
                                          console.error('Contact request error:', error);
                                          alert('Talep oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.');
                                        }
                                      } else if (button.url) {
                                        // Normal URL butonu
                                        window.open(button.url, '_blank', 'noopener,noreferrer');
                                      }
                                    }}
                                    className={`px-4 py-2 text-white rounded-lg transition-colors font-medium ${
                                      button.type === 'create-contact-request' 
                                        ? 'bg-green-600 hover:bg-green-700' 
                                        : 'bg-blue-600 hover:bg-blue-700'
                                    }`}
                                  >
                                    {button.label || button.content || (button.type === 'create-contact-request' ? 'İletişim Talebi Oluştur' : 'Paket Satın Al')}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    
                    {message?.suggestions && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {message.suggestions.map((suggestion, idx) => {
                          // Eğer suggestion bir obje ise
                          let suggestionText = typeof suggestion === 'object' && suggestion !== null 
                            ? suggestion.text || suggestion.action || suggestion
                            : suggestion;
                          // ** işaretlerini kaldır (bold markdown)
                          suggestionText = String(suggestionText).replace(/\*\*/g, '').trim();
                          const suggestionType = typeof suggestion === 'object' && suggestion !== null 
                            ? suggestion.type 
                            : 'question';
                          
                          return (
                            <button
                              key={idx}
                              onClick={() => handleSuggestionClick(suggestion)}
                              className={`px-2 py-1 text-xs rounded-full transition-colors ${
                                suggestionType === 'link' 
                                  ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                                  : 'bg-[#7A5AFF]/15 text-[#7A5AFF] hover:bg-[#7A5AFF]/25'
                              }`}
                            >
                              {suggestionType === 'link' && '🔗 '}
                              {suggestionText}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-white rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm border border-gray-100">
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-[#7A5AFF] rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-[#7A5AFF] rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                      <div className="w-2 h-2 bg-[#7A5AFF] rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                      <span className="text-xs text-gray-500 ml-2">Gogi yazıyor...</span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Admin typing indicator */}
              {adminTyping && (
                <div className="flex justify-start">
                  <div className="max-w-xs order-1">
                    <div className="mb-1 px-2">
                      <span className="text-xs font-semibold text-gray-600">Website Al Müşteri Danışmanı</span>
                    </div>
                    <div className="bg-white rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm border border-gray-100">
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-orange-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-orange-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                        <div className="w-2 h-2 bg-orange-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                        <span className="text-xs text-gray-500 ml-2">yazıyor...</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
            )}

            {/* Enhanced Input - Sadece form tamamlandıktan sonra göster ve konuşma tamamlanmamışsa */}
            {!showCustomerForm && cookieAccepted && !isConversationEnded && (
            <div className="p-4 border-t border-gray-200 bg-white rounded-b-lg">
              <form onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }} className="flex items-center space-x-2">
                <div className="flex-1 relative">
                  <Input
                    type="text"
                    placeholder="Gogi'ye sorunuzu yazın... (örn: e-ticaret paketi öner)"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    className="pr-12 h-12 rounded-full border-gray-300 focus:border-[#7A5AFF] focus:ring-2 focus:ring-[#7A5AFF]/20"
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <Sparkles className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  className="bg-gradient-to-r from-[#7A5AFF] to-[#5a3fd9] hover:from-[#8b6bff] hover:to-[#6b4de6] rounded-full p-3 transform hover:scale-105 transition-all duration-300 shadow-[#7A5AFF]/30"
                  disabled={!newMessage.trim()}
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
              
              {/* Power indicator */}
              <div className="flex items-center justify-center mt-2">
                <div className="flex items-center space-x-1 text-xs text-gray-500">
                  <Zap className="w-3 h-3 text-yellow-500" />
                  <span>Powered by Gogi AI</span>
                  <Star className="w-3 h-3 text-yellow-500 fill-current" />
                </div>
              </div>
            </div>
            )}
            
            {/* Konuşma Tamamlandı Mesajı */}
            {isConversationEnded && (
              <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                  <p className="text-sm text-yellow-800">
                    ⚠️ Bu konuşma tamamlandı. Yeni bir konuşma başlatmak için "Yeni Sohbet" butonuna tıklayın.
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Geçmiş konuşmalar modali kaldırıldı */}


    </div>
  );
};

export default EnhancedGogiWidget;